// ============================================================
// THE HAND CAMERA EASES (plan design-gaps, T5): on the display a wheel notch,
// a preset and an arrow key set a GOAL the camera glides to - a share of the
// gap each frame, the yaw the short way round - instead of jumping; and the
// wheel zooms toward the ground under the cursor, not the middle of the view.
//
// Pure. Zero React dependency. Scene units for the cursor.
// ============================================================

import type { Camera } from '../battlefield/projection';
import { SCENE_VIEW, panBy, unprojectToPlane, zoomBy } from '../battlefield/projection';
import { approach, lerpYaw, yawDelta } from './timeline';

/** The share of the gap a frame closes: about a fifth of a second to all but arrive at 60 fps. */
const EASE_K = 0.22;

/**
 * One frame toward the goal: the yaw the short way, the pitch, the zoom and the target each a share of their gap. The
 * GOAL ITSELF once everything has arrived, so a caller can tell the glide is over by identity.
 */
export function stepCamera(c: Camera, goal: Camera, k = EASE_K): Camera {
  const turn = yawDelta(c.yaw, goal.yaw);
  const next: Camera = {
    ...goal,
    yaw: Math.abs(turn) < 0.05 ? goal.yaw : lerpYaw(c.yaw, goal.yaw, k),
    pitch: approach(c.pitch, goal.pitch, k),
    zoom: approach(c.zoom, goal.zoom, k),
    target: { x: approach(c.target.x, goal.target.x, k), z: approach(c.target.z, goal.target.z, k) },
  };
  const arrived =
    next.yaw === goal.yaw &&
    next.pitch === goal.pitch &&
    next.zoom === goal.zoom &&
    next.target.x === goal.target.x &&
    next.target.z === goal.target.z;
  return arrived ? goal : next;
}

/**
 * Zoom by `factor` about a point of the screen (scene units) whose ground is `ground`: that ground stays under it, so
 * the wheel zooms toward the cursor. The caller reads `ground` off the camera on the screen (mid-glide, not the goal);
 * with none (the cursor over the sky), about the middle as before.
 */
export function zoomAbout(
  c: Camera,
  factor: number,
  at: { x: number; y: number },
  ground: { x: number; z: number } | null,
): Camera {
  const zoomed = zoomBy(c, factor);
  const after = unprojectToPlane(at.x, at.y, 0, zoomed, SCENE_VIEW);
  if (!ground || !after) return zoomed;
  return panBy(zoomed, ground.x - after.x, ground.z - after.z);
}
